/**
 * @fileoverview BiometricSetupPrompt Component for Clockwise CPA
 *
 * Shows a one-time modal prompting users to set up passkey authentication
 * (Touch ID, Face ID, or Windows Hello) after their first successful login.
 * Uses localStorage to track if the user has seen and responded to this prompt.
 *
 * Key Features:
 * - One-time display per user (tracked in localStorage)
 * - Beautiful gradient UI with benefits list
 * - Loading state management
 * - Navigation to settings page on acceptance
 *
 * @author Clockwise CPA Development Team
 * @version 1.0.0
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FiShield, FiX, FiCheck } from 'react-icons/fi';
import '../styles/BiometricSetupPrompt.css';

const BiometricSetupPrompt = ({ onSetup, onDismiss, isOpen }) => {
  const [loading, setLoading] = useState(false);

  const handleSetup = async () => {
    setLoading(true);
    try {
      await onSetup();
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="biometric-prompt-overlay">
      <div className="biometric-prompt-modal">
        <button
          className="biometric-prompt-close"
          onClick={onDismiss}
          disabled={loading}
          aria-label="Close"
        >
          <FiX />
        </button>

        <div className="biometric-prompt-icon">
          <FiShield />
        </div>

        <h2 className="biometric-prompt-title">
          Secure Your Account with Passkeys
        </h2>

        <p className="biometric-prompt-description">
          Enable passkey authentication using Touch ID, Face ID, or Windows Hello for faster and more secure logins.
          You can only register one passkey at a time.
        </p>

        <div className="biometric-prompt-benefits">
          <div className="biometric-prompt-benefit">
            <FiCheck className="biometric-prompt-benefit-icon" />
            <span>Faster login experience</span>
          </div>
          <div className="biometric-prompt-benefit">
            <FiCheck className="biometric-prompt-benefit-icon" />
            <span>More secure than passwords</span>
          </div>
          <div className="biometric-prompt-benefit">
            <FiCheck className="biometric-prompt-benefit-icon" />
            <span>No codes to remember</span>
          </div>
        </div>

        <div className="biometric-prompt-actions">
          <button
            className="biometric-prompt-setup-btn"
            onClick={handleSetup}
            disabled={loading}
          >
            {loading ? 'Setting Up...' : 'Set Up Passkey Login'}
          </button>
          <button
            className="biometric-prompt-skip-btn"
            onClick={onDismiss}
            disabled={loading}
          >
            Maybe Later
          </button>
        </div>

        <p className="biometric-prompt-note">
          You can always set this up later in Settings
        </p>
      </div>
    </div>
  );
};

BiometricSetupPrompt.propTypes = {
  onSetup: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired
};

export default BiometricSetupPrompt;
